import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { DatePicker } from '@/components/ui/date-picker';
import { ContractorsForm, CONTRACTORS_FORM_ACTION_CREATE } from '@/components/contractors/ContractorsForm';
import FileUploader from '@/components/global/FileUploader';
import OneTimeContractProgressBar from '@/components/one-time-contract/OneTimeContractProgressBar';
import { SERVICE_CATEGORIES, COPYRIGHT_TYPES, CONTRACT_TEMPLATES, generateContractPreview } from '@/constants/oneTimeContract';
import { InfoIcon, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { validateNoneCyrillic, GET_COUNTRY_NAME_BY_CODE, SUPPORTED_CURRENCIES, transformFromIdValueToValueLabel } from '@/constants';
import { format } from 'date-fns';
import { getOneTimeContractById, updateOneTimeContract, uploadFiles, attachFilesToOneTimeContract, getUser, calculateOneTimeContractAmounts } from '@/service/weexpertService';
import { toast } from '@/components/ui/use-toast';
import ExistingFilesList from '@/components/one-time-contract/ExistingFilesList';
import CalculationBlock, { useContractCalculations } from './one-time-calculation-cards';

export const OneTimeContractEditPage = () => {
  const { t } = useTranslation(['translation', 'contracts']);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStage, setCurrentStage] = useState(2); // Start with contract details since counterparty can't be changed
  const [inputChecked, setInputChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Contract Details State
  const [serviceCategory, setServiceCategory] = useState(null);
  const [serviceSubcategory, setServiceSubcategory] = useState(null);
  const [contractTitle, setContractTitle] = useState('');
  const [contractDescription, setContractDescription] = useState('');
  const [copyrightType, setCopyrightType] = useState(null);
  const [priceNetto, setPriceNetto] = useState('');
  const [totalAmountToPay, setTotalAmountToPay] = useState('');
  const [clearHandMoney, setClearHandMoney] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState(null);
  const [messageForCounterparty, setMessageForCounterparty] = useState('');
  const [messageToEmail, setMessageToEmail] = useState('');
  const [wantToSendMessage, setWantToSendMessage] = useState(false);
  const [currency, setCurrency] = useState('PLN');
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [userBankAccounts, setUserBankAccounts] = useState([]);

  // Input states for immediate feedback (separate from calculated values)
  const [nettoInput, setNettoInput] = useState('');
  const [bruttoInput, setBruttoInput] = useState('');
  const [clearHandInput, setClearHandInput] = useState('');

  // Timer reference for debouncing
  const debounceTimer = useRef(null);

  // Hint state for copyright type requirement
  const [showCopyrightHint, setShowCopyrightHint] = useState(false);

  // Files State
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Calculation state
  const [counterpartyCalculation, setCounterpartyCalculation] = useState(null);
  const [contractorCalculation, setContractorCalculation] = useState(null);

  // Get current user data for bank accounts
  const { data: authData } = useQuery({
    queryKey: 'userMe',
    queryFn: getUser,
    retry: false,
    refetchOnWindowFocus: false
  });

  // Extract and format CLIENT bank accounts from user data
  useEffect(() => {
    if (authData?.data?.bankAccounts) {
      const clientAccounts = authData.data.bankAccounts
        .filter(account => account.accountType === 'CLIENT')
        .map(account => ({
          value: account.id,
          label: `${account.account} (${account.currency})`,
          account: account.account,
          currency: account.currency,
          swift: account.swift
        }));
      setUserBankAccounts(clientAccounts);
    }
  }, [authData]);

  // Helper functions for options - using useCallback to prevent unnecessary re-renders
  const getCategoryOptions = React.useCallback(() => {
    const categoryKeyMap = {
      'education': 'education_services',
      'sport': 'sport',
      'programming': 'programming_it',
      'multimedia': 'photography_graphics',
      'texts': 'texts_translations',
      'music': 'music',
      'other': 'other'
    };

    return Object.values(SERVICE_CATEGORIES).map(cat => ({
      value: cat.id,
      label: t(`contracts:${categoryKeyMap[cat.id]}`)
    }));
  }, [t]);

  // Mapping for copyright types to translation keys
  const copyrightKeyMap = {
    'COPYRIGHT': 'copyright_rights',
    'NO_TRANSFER': 'no_copyright_transfer'
  };
  const copyrightDescMap = {
    'COPYRIGHT': 'copyright_description',
    'NO_TRANSFER': 'no_transfer_description'
  };

  const getCopyrightOptions = React.useCallback(() => {
    return Object.values(COPYRIGHT_TYPES).filter(type => type.id !== 'LICENSE').map(type => ({
      value: type.id,
      label: t(`contracts:${copyrightKeyMap[type.id]}`)
    }));
  }, [t]);

  // Fetch contract data
  const { data: contractData, isLoading, error } = useQuery({
    queryKey: ['oneTimeContract', id],
    queryFn: () => getOneTimeContractById(id),
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const contract = data.data.contract;
      console.log(contract)
      // Set simple fields immediately
      setContractTitle(contract.title || '');
      setContractDescription(contract.description || '');
      setPriceNetto(contract.priceNetto);
      setPaymentDueDate(contract.paymentDueDate ? new Date(contract.paymentDueDate) : null);
      setMessageForCounterparty(contract.messageForCounterparty || '');
      setMessageToEmail(contract.counterpartyEmail || '');
      setWantToSendMessage(!!(contract.messageForCounterparty || contract.counterpartyEmail));
      const categoryObject = Object.values(SERVICE_CATEGORIES).find(e => e.id == contract.serviceCategory);
      if (categoryObject) {
        setServiceCategory(transformFromIdValueToValueLabel(categoryObject))
        setServiceSubcategory(transformFromIdValueToValueLabel(Object.values(categoryObject.subcategories).find(e => e.id == contract.serviceSubcategory)))
      }
      if (userBankAccounts) {
        const bankAccountFromTheContract = userBankAccounts.find(e => e.account == contract.bankAccount);
        if (bankAccountFromTheContract) {
          setSelectedBankAccountId(bankAccountFromTheContract.value)
        }
      }
      if (contract.copyrightType) {
        const copyrightOptions = getCopyrightOptions();
        const selectedCopyright = copyrightOptions.find(opt => opt.value === contract.copyrightType);
        if (selectedCopyright) {
          setCopyrightType(selectedCopyright);
        }
      }

      setCurrency(contract.currency);
      setPriceNetto(contract.priceNetto)

      handleAmountChange('netto', contract.priceNetto?.toString() || '');
    }
  });

  // Get calculation data from API
  const {
    data: calculations,
    isLoading: calculationsLoading,
    error: calculationsError
  } = useContractCalculations(
    priceNetto,
    copyrightType?.value,
    contractData?.data?.contract?.counterparty?.id,
    typeof currency === 'object' ? currency?.value : currency || 'PLN'
  );

  // Initialize bank account after both contract data and user bank accounts are loaded
  useEffect(() => {
    if (contractData?.data?.contract && userBankAccounts.length > 0) {
      const contract = contractData.data.contract;

      // Find the bank account that matches the stored account string
      if (contract.bankAccount) {
        const matchingBankAccount = userBankAccounts.find(
          acc => acc.account === contract.bankAccount
        );

        if (matchingBankAccount) {
          setSelectedBankAccountId(matchingBankAccount.value);
        }
      }
    }
  }, [contractData, userBankAccounts]);

    // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Hide copyright hint after 3 seconds
  useEffect(() => {
    if (showCopyrightHint) {
      const timer = setTimeout(() => {
        setShowCopyrightHint(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCopyrightHint]);

  // Update calculations and form fields when API calculations change (matching create page)
  useEffect(() => {
    if (calculations) {
      // Set calculation state from API response
      setCounterpartyCalculation(calculations.counterparty);
      setContractorCalculation(calculations.contractor);

      // Update amount fields based on API calculations
      if (priceNetto && !isNaN(parseFloat(priceNetto))) {
        const bruttoValue = calculations.counterparty?.brutto?.toFixed(2) || '';
        const clearValue = calculations.contractor?.toPay?.toFixed(2) || '';

        setTotalAmountToPay(bruttoValue);
        setClearHandMoney(clearValue);

        // Also sync input states to prevent drift
        setNettoInput(priceNetto);
        setBruttoInput(bruttoValue);
        setClearHandInput(clearValue);
      }
    } else {
      // Clear calculations when no data available
      setCounterpartyCalculation(null);
      setContractorCalculation(null);
    }
  }, [calculations, priceNetto]);

  // Update contract mutation
  const updateMutation = useMutation({
    mutationFn: async (contractUpdateData) => {
      return await updateOneTimeContract(id, contractUpdateData);
    },
    onError: (error) => {
      toast({
        title: t('contracts:error'),
        description: error.response?.data?.message || t('contracts:failed_to_update_contract'),
        variant: "destructive",
      });
    }
  });


  // Simple calculation helper functions (matching create page)

  const getTaxDeductibleRate = (copyrightType) => {
    if (copyrightType === 'LICENSE' || copyrightType === 'COPYRIGHT') {
      return 0.5; // 50%
    }
    if (copyrightType === 'NO_TRANSFER') {
      return 0.2; // 20%
    }
    return 0;
  };


  // Reverse calculation: from brutto to netto
  const calculateNettoFromBrutto = (bruttoAmount, vatRate) => {
    if (!bruttoAmount || isNaN(parseFloat(bruttoAmount))) return null;
    const brutto = parseFloat(bruttoAmount);
    const netto = brutto / (1 + vatRate);
    return netto;
  };

  // Reverse calculation: from clear hand money to netto (simplified version from create page)
  const calculateNettoFromClearHand = (clearHandAmount, copyrightTypeValue, vatRate) => {
    if (!clearHandAmount || isNaN(parseFloat(clearHandAmount)) || !copyrightTypeValue) return null;

    const clearHand = parseFloat(clearHandAmount);
    const taxDeductibleRate = getTaxDeductibleRate(copyrightTypeValue);

    // Reverse calculation: clearHand = baseForContractor - pitAmount
    // where pitAmount = (baseForContractor - baseForContractor * taxDeductibleRate) * 0.12
    // Solving for baseForContractor:
    const baseForContractor = clearHand / (1 - 0.12 * (1 - taxDeductibleRate));

    // Simplified approach: assume 65 PLN fixed fee as starting point
    // The API calculations will provide the accurate result
    const netto = baseForContractor + 25;

    return netto;
  };

  // Simplified debounced calculation function (matching create page)
  const debouncedCalculate = (field, value) => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      const origin = contractData?.data?.contract?.counterparty?.origin || 'PL';
      const counterpartyType = contractData?.data?.contract?.counterparty?.counterpartyType || 'INDIVIDUAL';
      const vatRate = (origin === 'PL' || counterpartyType === 'INDIVIDUAL') ? 0.23 : 0;

      if (field === 'netto') {
        // For netto input, just set the value - API calculations will handle the rest
        setPriceNetto(value);
        if (!value || isNaN(parseFloat(value))) {
          // Clear other fields if netto is invalid
          setTotalAmountToPay('');
          setClearHandMoney('');
          setBruttoInput('');
          setClearHandInput('');
        }
      } else if (field === 'brutto') {
        // Calculate netto from brutto and set it - API will handle rest
        if (value && !isNaN(parseFloat(value))) {
          const netto = calculateNettoFromBrutto(value, vatRate);
          if (netto) {
            const nettoValue = netto.toFixed(2);
            setPriceNetto(nettoValue);
            setNettoInput(nettoValue);
          } else {
            setPriceNetto('');
            setClearHandMoney('');
            setNettoInput('');
            setClearHandInput('');
          }
        } else {
          setPriceNetto('');
          setClearHandMoney('');
          setNettoInput('');
          setClearHandInput('');
        }
      } else if (field === 'clearHand') {
        // Calculate netto from clearHand and set it - API will handle rest
        if (value && !isNaN(parseFloat(value)) && copyrightType?.value) {
          const netto = calculateNettoFromClearHand(value, copyrightType?.value, vatRate);
          if (netto) {
            const nettoValue = netto.toFixed(2);
            setPriceNetto(nettoValue);
            setNettoInput(nettoValue);
          } else {
            setPriceNetto('');
            setTotalAmountToPay('');
            setNettoInput('');
            setBruttoInput('');
          }
        } else {
          setPriceNetto('');
          setTotalAmountToPay('');
          setNettoInput('');
          setBruttoInput('');
        }
      }
    }, 500); // 500ms debounce delay
  };

  // Handle amount field changes with immediate input feedback
  const handleAmountChange = (field, value) => {
    // Update input states immediately for responsive UI
    if (field === 'netto') {
      setNettoInput(value);
    } else if (field === 'brutto') {
      setBruttoInput(value);
    } else if (field === 'clearHand') {
      setClearHandInput(value);
    }

    // Trigger debounced calculation
    debouncedCalculate(field, value);
  };

  const getSubcategoryOptions = () => {
    if (!serviceCategory || !serviceCategory.value) return [];
    const category = Object.values(SERVICE_CATEGORIES).find(cat => cat.id === serviceCategory.value);
    return category ? category.subcategories.map(sub => ({
      value: sub.id,
      label: t(`contracts:${sub.id}`)
    })) : [];
  };

  // Generate options - these will be computed on each render but that's fine for small lists
  const categoryOptions = React.useMemo(() => {
    const options = getCategoryOptions();
    console.log('🔍 DEBUG: Memoized categoryOptions:', options);
    return options;
  }, [getCategoryOptions]);

  const subcategoryOptions = React.useMemo(() => {
    const options = getSubcategoryOptions();
    console.log('🔍 DEBUG: Memoized subcategoryOptions:', options);
    return options;
  }, [getSubcategoryOptions, serviceCategory]);

  const copyrightOptions = React.useMemo(() => getCopyrightOptions(), [getCopyrightOptions]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:edit_contract_title')}</div>
        <Card className="p-6">
          <div className="text-center">{t('common:loading')}</div>
        </Card>
      </div>
    );
  }

  // Handle error state
  if (error || !contractData?.data?.contract) {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:edit_contract_title')}</div>
        <Card className="p-6">
          <div className="text-center">
            <p className="text-red-500 mb-4">{t('contracts:error_loading_contract')}</p>
            <Button
              onClick={() => navigate('/contracts/one-time')}
              variant="outline"
            >
              {t('common:back_to_list')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const contract = contractData.data.contract;

  // Check if contract can be edited
  if (['AWAITING_APPROVAL', 'WAITING_FOR_CHANGE'].includes(contract.status) == false) {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:edit_contract_title')}</div>
        <Card className="p-6">
          <div className="text-center">
            <p className="text-red-500 mb-4">{t('contracts:contract_status_not_editable', { status: contract.status })}</p>
            <Button
              onClick={() => navigate(`/contracts/one-time/${id}`)}
              variant="outline"
            >
              {t('contracts:back_to_contract_view')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleSubmit = async () => {
    setInputChecked(true);
    setIsSubmitting(true);

    // Validate required fields
    if (!serviceCategory || !serviceSubcategory || !contractTitle.trim() || !contractDescription.trim() || !copyrightType || !priceNetto || isNaN(parseFloat(priceNetto)) || !paymentDueDate || !selectedBankAccountId) {
      setIsSubmitting(false);
      return;
    }

    try {
      // First update the contract
      const contractUpdateData = {
        serviceCategory: serviceCategory?.value,
        serviceSubcategory: serviceSubcategory?.value,
        title: contractTitle,
        description: contractDescription,
        copyrightType: copyrightType?.value,
        priceNetto: parseFloat(priceNetto),
        paymentDueDate: format(paymentDueDate, 'yyyy-MM-dd'),
        messageForCounterparty: wantToSendMessage ? messageForCounterparty || undefined : undefined,
        counterpartyEmail: wantToSendMessage ? messageToEmail || undefined : undefined,
        // Only include currency if it's not PLN (matching create page logic)
        ...(currency !== 'PLN' && { currency: typeof currency === 'object' ? currency?.value : currency }),
        // Extract the account string from the selected bank account
        bankAccount: userBankAccounts.find(acc => acc.value === selectedBankAccountId)?.account || null
      };

      await updateMutation.mutateAsync(contractUpdateData);

      // Then attach files if any are selected
      if (selectedFiles.length > 0) {
        const uploadResponse = await uploadFiles(selectedFiles);
        const fileIds = uploadResponse.data.files.map(file => file.id);
        await attachFilesToOneTimeContract(id, fileIds);
        setSelectedFiles([]); // Clear selected files after successful upload
      }

      // Show success message and refresh data
      toast({
        title: t('contracts:success'),
        description: selectedFiles.length > 0 ?
          t('contracts:contract_updated_with_files') :
          t('contracts:contract_updated_successfully'),
      });

      queryClient.invalidateQueries(['oneTimeContract', id]);

      // Navigate after both operations complete
      navigate(`/contracts/one-time/${id}`);
    } catch (error) {
      console.error('Error saving contract:', error);
      toast({
        title: t('contracts:error'),
        description: error.response?.data?.message || t('contracts:failed_to_save'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  //data for calculation block
  const counterparty = contract?.counterparty;
  const nettoValue = parseFloat(priceNetto) || 0;
  const selectedCurrency = currency;
  const copyright = copyrightType;

  return (
    <div className="w-full px-8 py-10 bg-grayLightMainBg">
      <div className="text-4xl mb-8">{t('contracts:edit_contract_title')} {contract.contractNumber}</div>

      <OneTimeContractProgressBar currentStage={currentStage} />

      <div className="w-full lg:w-9/12 pt-8">
        <h2 className="text-2xl font-medium mb-6">{t('contracts:contract_details_title')}</h2>

        {/* Counterparty Information (Read-only) */}
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">{t('contracts:counterparty_data')} ({t('contracts:read_only')})</h3>
          <div className="w-full flex justify-between">
            <div className="w-[49%]">
              <InputWithAlert
                label={t('contracts:counterparty_name')}
                value={contract.counterparty?.name || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('contracts:origin')}
                value={contract.counterparty?.origin === 'OTHER' ? t('contracts:rest_of_world') :
                  (contract.counterparty?.origin === 'EU' ? t('contracts:eu') : t('contracts:poland'))}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              {contract.counterparty?.counterpartyType === 'COMPANY' && (
                <InputWithAlert
                  label={t('contracts:nip')}
                  value={contract.counterparty?.NIP || ''}
                  onChange={() => {}}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
              <InputWithAlert
                label={t('contracts:email')}
                value={contract.counterparty?.email || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
            <div className="w-[49%]">
              <InputWithAlert
                label={t('contracts:type')}
                value={contract.counterparty?.counterpartyType === 'COMPANY' ? t('contracts:company') : t('contracts:individual')}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('contracts:address')}
                value={contract.counterparty?.address || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('contracts:postal_code')}
                value={contract.counterparty?.postIndex || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('contracts:city')}
                value={contract.counterparty?.city || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('contracts:country')}
                value={GET_COUNTRY_NAME_BY_CODE(contract.counterparty?.country) || ''}
                onChange={() => {}}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          {/* Service Category */}
          <div className="flex gap-4">
            <div className="w-1/2">
              {(() => {
                console.log('🔍 DEBUG: Rendering category Combobox with:', {
                  value: serviceCategory,
                  options: categoryOptions,
                  valueType: typeof serviceCategory,
                  valueStructure: serviceCategory ? Object.keys(serviceCategory) : 'null'
                });
                return null;
              })()}
              <Combobox
                label={t('contracts:service_category_label')}
                value={serviceCategory}
                placeholder={t('contracts:select_category')}
                options={categoryOptions}
                onChange={(value) => {
                  console.log('🔍 DEBUG: Category onChange called with:', value);
                  console.log('🔍 DEBUG: Stack trace:', new Error().stack);
                  if (value !== null) { // Only update if not null
                    setServiceCategory(value);
                    setServiceSubcategory(null);
                  }
                }}
                error={inputChecked && !serviceCategory}
                errorMessage={t('contracts:field_required')}
              />
            </div>
            <div className="w-1/2">
              {(() => {
                console.log('🔍 DEBUG: Rendering subcategory Combobox with:', {
                  value: serviceSubcategory,
                  options: subcategoryOptions,
                  valueType: typeof serviceSubcategory,
                  valueStructure: serviceSubcategory ? Object.keys(serviceSubcategory) : 'null'
                });
                return null;
              })()}
              <Combobox
                label={t('contracts:subcategory')}
                value={serviceSubcategory}
                placeholder={t('contracts:select_subcategory')}
                options={subcategoryOptions}
                onChange={(value) => {
                  console.log('🔍 DEBUG: Subcategory onChange called with:', value);
                  console.log('🔍 DEBUG: Stack trace:', new Error().stack);
                  if (value !== null) { // Only update if not null
                    setServiceSubcategory(value);
                  }
                }}
                disabled={!serviceCategory}
                error={inputChecked && !serviceSubcategory}
                errorMessage={t('contracts:field_required')}
              />
            </div>
          </div>

          {/* Title and Description */}
          <div>
            <InputWithAlert
              label={t('contracts:contract_title')}
              value={contractTitle}
              onChange={(e) => setContractTitle(e.target.value)}
              placeholder={t('contracts:enter_contract_title')}
              error={inputChecked && !contractTitle.trim()}
              errorMessage={t('contracts:field_required')}
            />
            <div className="flex items-center gap-2 mt-1 text-xs text-blue-600">
              <InfoIcon className="w-3 h-3" />
              <span>{t('contracts:polish_language_note')}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('contracts:contract_description')}</label>
            <textarea
              className={`w-full px-3 py-2 border rounded-md resize-none ${
                inputChecked && !contractDescription.trim() ? 'border-red-500' : 'border-gray-300'
              }`}
              rows={4}
              value={contractDescription}
              onChange={(e) => setContractDescription(e.target.value)}
              placeholder={t('contracts:describe_contract_details')}
            />
            {inputChecked && !contractDescription.trim() && (
              <p className="text-red-500 text-sm mt-1">{t('contracts:field_required')}</p>
            )}
            <div className="flex items-center gap-2 mt-1 text-xs text-blue-600">
              <InfoIcon className="w-3 h-3" />
              <span>{t('contracts:polish_language_note')}</span>
            </div>
          </div>

          {/* Contract Preview */}
          {serviceCategory && serviceSubcategory && (
            <Card className="p-4 bg-gray-50 border-l-4 border-blue-500">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('contracts:contract_preview')}</h4>
              <p className="text-sm text-gray-800 leading-relaxed font-mono">
                {generateContractPreview(
                  serviceCategory?.value,
                  serviceSubcategory?.value,
                  contractTitle,
                  contractDescription
                )}
              </p>
            </Card>
          )}

          {/* Message for Counterparty */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="wantToSendMessage"
                checked={wantToSendMessage}
                onChange={(e) => {
                  setWantToSendMessage(e.target.checked);
                  if (!e.target.checked) {
                    setMessageForCounterparty('');
                    setMessageToEmail('');
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="wantToSendMessage" className="text-sm font-medium text-gray-700">
                {t('contracts:send_message_to_counterparty_checkbox')}
              </label>
            </div>

            {wantToSendMessage && (
              <div className="space-y-4 pl-6 border-l-2 border-gray-200">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('contracts:counterparty_email_input_label')}</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={messageToEmail}
                    onChange={(e) => setMessageToEmail(e.target.value)}
                    placeholder={t('contracts:counterparty_email_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('contracts:message_for_counterparty_input_label')}</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    value={messageForCounterparty}
                    onChange={(e) => setMessageForCounterparty(e.target.value)}
                    placeholder={t('contracts:additional_message_placeholder')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Copyright Type */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('contracts:copyright_transfer_type')}</label>
            <div className="space-y-3">
              {Object.values(COPYRIGHT_TYPES).filter(type => type.id !== 'LICENSE').map((type) => (
                <Card
                  key={type.id}
                  className={`p-4 cursor-pointer border-2 transition-colors ${
                    copyrightType?.value === type.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => {
                    setCopyrightType({ value: type.id, label: t(`contracts:${copyrightKeyMap[type.id]}`) });
                    // Clear all amount fields when copyright type changes
                    if (copyrightType?.value !== type.id) {
                      setPriceNetto('');
                      setTotalAmountToPay('');
                      setClearHandMoney('');
                      setNettoInput('');
                      setBruttoInput('');
                      setClearHandInput('');
                      setCounterpartyCalculation(null);
                      setContractorCalculation(null);
                    }
                  }}
                >
                  <div className="flex items-start">
                    <div className="flex-1">
                      <h4 className="font-medium">{t(`contracts:${copyrightKeyMap[type.id]}`)}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t(`contracts:${copyrightDescMap[type.id]}`)}</p>
                    </div>
                    <InfoIcon className="w-5 h-5 text-gray-400 ml-2" />
                  </div>
                </Card>
              ))}
            </div>
            {inputChecked && !copyrightType && (
              <p className="text-red-500 text-sm mt-1">{t('contracts:select_copyright_type')}</p>
            )}
          </div>

          {/* Currency and Bank Account */}
          <div className="flex gap-4">
            <div className="w-1/2">
              <Combobox
                label={t('invoices:currency')}
                value={typeof currency === 'object' ? currency : SUPPORTED_CURRENCIES.find(c => c.value === currency) || { value: currency, label: currency }}
                placeholder={t('signup:select_currency')}
                options={SUPPORTED_CURRENCIES}
                onChange={(value) => {
                  const currencyValue = value?.value || value;
                  setCurrency(currencyValue);
                  // Clear amount fields when currency changes
                  setPriceNetto('');
                  setTotalAmountToPay('');
                  setClearHandMoney('');
                  setNettoInput('');
                  setBruttoInput('');
                  setClearHandInput('');
                }}
                error={inputChecked && !currency}
                errorMessage={t('contracts:field_required')}
              />
            </div>
            <div className="w-1/2">
              <Combobox
                label={t('signup:bank_account')}
                value={userBankAccounts.find(acc => acc.value === selectedBankAccountId) || null}
                placeholder={t('signup:select_bank_account')}
                options={userBankAccounts}
                onChange={(value) => setSelectedBankAccountId(value?.value || '')}
                error={inputChecked && !selectedBankAccountId}
                errorMessage={t('contracts:field_required')}
              />
              {/* Currency mismatch warning */}
              {selectedBankAccountId && userBankAccounts.find(acc => acc.value === selectedBankAccountId)?.currency !== currency && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    {t('contracts:bank_account_currency_mismatch')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Price Fields */}
          {(() => {
            const selectedCurrency = typeof currency === 'object' ? currency?.value : currency || 'PLN';

            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <InputWithAlert
                    label={`${t('contracts:client_will_pay')} (${selectedCurrency})`}
                    value={bruttoInput}
                    onChange={(e) => handleAmountChange('brutto', e.target.value)}
                    placeholder="0.00"
                    disabled={!copyrightType}
                    onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                    inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <InputWithAlert
                    label={`${t('contracts:client_will_pay_net')} (${selectedCurrency})`}
                    value={nettoInput}
                    onChange={(e) => handleAmountChange('netto', e.target.value)}
                    placeholder="0.00"
                    error={inputChecked && (!priceNetto || isNaN(parseFloat(priceNetto)))}
                    errorMessage={t('contracts:enter_valid_amount')}
                    disabled={!copyrightType}
                    onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                    inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <InputWithAlert
                    label={`${t('contracts:you_will_earn')} (${selectedCurrency})`}
                    value={clearHandInput}
                    onChange={(e) => handleAmountChange('clearHand', e.target.value)}
                    placeholder="0.00"
                    disabled={!copyrightType}
                    onFocus={() => !copyrightType && setShowCopyrightHint(true)}
                    inputClassName={`h-12 mt-1 ${!copyrightType ? 'cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
              </div>
            );
          })()}

          {/* Copyright Type Hint */}
          {showCopyrightHint && !copyrightType && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
              <InfoIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                {t('contracts:select_copyright_type_first')}
              </p>
            </div>
          )}

          {/* Payment Date */}
          <div className="w-full md:w-[49%]">
            <DatePicker
              date={paymentDueDate}
              setDate={setPaymentDueDate}
              label={t('contracts:payment_due_date')}
              disabled={{ before: new Date()}}
              error={inputChecked && !paymentDueDate}
              errorMessage={t('contracts:select_payment_date')}
            />
          </div>

          {/* Calculation Blocks */}
            <CalculationBlock
              priceNetto={nettoValue}
              currency={selectedCurrency}
              copyrightType={copyright}
              selectedCounterparty={counterparty}
              counterpartyOrigin={counterparty?.origin}
              counterpartyType={counterparty?.counterpartyType || counterparty?.type}
              t={t}
            />
        </div>

        {/* Files */}
        <div className="space-y-6 mt-8 ">
          <div>
            <label className="block text-sm font-medium mb-4">{t('contracts:contract_files')}</label>

            {/* Existing Files */}
            {contractData?.data?.contract?.files?.length > 0 && (
              <ExistingFilesList
                files={contractData.data.contract.files}
                contractId={id}
                onFileRemoved={() => queryClient.invalidateQueries(['oneTimeContract', id])}
              />
            )}

            {/* File Uploader for New Files */}
            <div className="space-y-2 py-10">
              <h5 className="text-sm font-medium text-gray-600">{t('contracts:add_new_files')}</h5>
              <FileUploader onChange={setSelectedFiles} />
              {selectedFiles.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  {t('contracts:save_changes_with_files', { count: selectedFiles.length })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => navigate(`/contracts/one-time/${id}`)}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('contracts:back')}
          </Button>

          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-8"
            >
              {isSubmitting ?
                t('contracts:saving') :
                (selectedFiles.length > 0 ?
                  t('contracts:save_changes_with_files', { count: selectedFiles.length }) :
                  t('contracts:save_changes')
                )
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneTimeContractEditPage;
